import React, {useState, useEffect, useRef} from "react";
import axios from "axios";
import {Button} from "primereact/button";
import {FloatLabel} from "primereact/floatlabel";
import {Toast} from "primereact/toast";
import {variables} from "../static/contracts_variables";
import {InputText} from "primereact/inputtext";
import {bootstrapColors, primeIcons} from "../static/colors_icons_ui";
import IconSelector from "./widgets/IconSelector";
import ColorSelector from "./widgets/ColorSelector";
import VariableSelector from "./widgets/VariableSelector";

import CreatingCard from "./widgets/CreatingCard";
import {base, get_contract_types} from "../../api";
import {FileUpload} from "primereact/fileupload";
import {ProgressBar} from "primereact/progressbar";
import {Tooltip} from "primereact/tooltip";
import DisabledFormFields from "./widgets/DisabledFormFields";

export default function ProcessesDocumentationAddForm({
	setDialogVisible,
	dialogVisible,
	fetchProcesses,
}) {
	const [selectedKeys, setSelectedKeys] = useState([]);
	const availableKeys = variables.filter(
		item => !selectedKeys.find(key => key.field_name === item.field_name),
	);
	const [inputValues, setInputValues] = useState({});
	const [formName, setFormName] = useState("");
	const [slug, setSlug] = useState("");
	const [selectedColor, setSelectedColor] = useState(bootstrapColors[0].color);
	const [selectedIcon, setSelectedIcon] = useState(primeIcons[0]);

	const toast = React.useRef(null);

	const handleChipClick = key => {
		setSelectedKeys([...selectedKeys, key]);
	};

	const handleRemoveInput = key => {
		setSelectedKeys(selectedKeys.filter(item => item.field_name !== key.field_name));
		setInputValues(prev => {
			const newValues = {...prev};
			delete newValues[key.field_name];
			return newValues;
		});
	};

	const handleInputChange = (variable, value) => {
		setInputValues(prev => ({...prev, [variable]: value}));
	};

	const saveData = async () => {
		const formData = new FormData();

		formData.append("name", formName);
		formData.append("slug", slug);
		formData.append("color", selectedColor);
		formData.append("icon", selectedIcon);
		formData.append("form_fields", JSON.stringify(selectedKeys));
		Object.keys(inputValues).forEach(key => {
			formData.append(`inputValues[${key}]`, inputValues[key]);
		});
		const files = fileUploadRef.current?.getFiles() || [];
		files.forEach(file => {
			formData.append("uploaded_files", file);
		});

		try {
			const response = await axios.post(base + get_contract_types, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			fetchProcesses();
			setDialogVisible(false);
			toast.current.show({
				severity: "success",
				summary: "Success",
				detail: "Data saved successfully",
			});
		} catch (error) {
			console.error("Error saving data:", error);
			toast.current.show({
				severity: "error",
				summary: "Error",
				detail: "Failed to save data",
			});
		}
	};

	const [totalSize, setTotalSize] = useState(0);
	const fileUploadRef = useRef(null);

	const onTemplateSelect = e => {
		let _totalSize = totalSize;
		let files = e.files;

		Object.keys(files).forEach(key => {
			_totalSize += files[key].size || 0;
		});

		setTotalSize(_totalSize);
	};

	const onTemplateUpload = e => {
		let _totalSize = 0;

		e.files.forEach(file => {
			_totalSize += file.size || 0;
		});

		setTotalSize(_totalSize);
		toast.current.show({severity: "info", summary: "Success", detail: "File Uploaded"});
	};

	const onTemplateRemove = (file, callback) => {
		setTotalSize(totalSize - file.size);
		callback();
	};

	const onTemplateClear = () => {
		setTotalSize(0);
	};

	const headerTemplate = options => {
		const {className, chooseButton} = options;
		const value = totalSize / 10000;
		const formatedValue =
			fileUploadRef && fileUploadRef.current ? fileUploadRef.current.formatSize(totalSize) : "0 B";

		return (
			<div
				className={className}
				style={{backgroundColor: "transparent", display: "flex", alignItems: "center"}}>
				{chooseButton}
				<div className="flex align-items-center gap-3 ml-auto">
					<span>{formatedValue} / 1 MB</span>
					<ProgressBar
						value={value}
						showValue={false}
						style={{width: "10rem", height: "12px"}}></ProgressBar>
				</div>
			</div>
		);
	};

	const itemTemplate = (file, props) => {
		return (
			<div className="w-100 d-flex justify-content-between align-items-center py-0">
				<span className="text-start text-primary">
					{file.name}
					<small className="text-info ms-1">({props.formatSize})</small>
				</span>
				<Button
					type="button"
					icon="pi pi-times"
					text
					rounded
					severity="danger"
					onClick={() => onTemplateRemove(file, props.onRemove)}
				/>
			</div>
		);
	};

	const emptyTemplate = () => {
		return (
			<div className="flex align-items-center flex-column">
				<i
					className="pi pi-image mt-2 p-3"
					style={{
						fontSize: "1.5em",
						borderRadius: "50%",
						backgroundColor: "var(--surface-b)",
						color: "var(--surface-d)",
					}}></i>
				<span style={{fontSize: "1.2em", color: "var(--text-color-secondary)"}} className="mt-2">
					Drag and Drop Image Here
				</span>
			</div>
		);
	};

	const chooseOptions = {
		icon: "pi pi-arrow-up",
		iconOnly: true,
		className: "custom-choose-btn p-button-rounded p-button-outlined",
	};
	useEffect(() => {
		const dialogContent = document.querySelector(".p-dialog .p-dialog-content");
		if (dialogContent) {
			dialogContent.scrollTop = 0;
		}
	}, [dialogVisible]);
	return (
		<div className="col-12">
			<Toast ref={toast} />
			<div className="row">
				<div className="col-12 fs-5 fw-bold opacity-50 border-bottom pb-2 mb-2 p-0">
					Card example
				</div>

				<div className="col-xl-3 col-lg-4 col-md-4 col-12 stretch-card p-3">
					<CreatingCard
						selectedIcon={selectedIcon}
						formName={formName}
						selectedColor={selectedColor}
					/>
				</div>
				<div className="col-xl-9 col-lg-8 col-md-8 col-12 stretch-card p-3">
					<div className="row">
						<FloatLabel className="col-6">
							<InputText
								className="w-100"
								id="formName"
								value={formName}
								onChange={e => {
									setFormName(e.target.value);
									setSlug(
										e.target.value
											.toLowerCase()
											.replace(/\s+/g, "-") // Replace spaces with '-'
											.replace(/[^a-z0-9_-]/g, ""), // Allow lowercase letters, numbers, '_' and '-'
									);
								}}
							/>
							<label htmlFor="formName" className="ps-3 pt-0">
								Card Title
							</label>
						</FloatLabel>
						<FloatLabel className="col-6">
							<InputText
								className="w-100"
								id="slug"
								value={slug}
								onChange={e =>
									setSlug(
										e.target.value
											.toLowerCase()
											.replace(/\s+/g, "-") // Replace spaces with '-'
											.replace(/[^a-z0-9_-]/g, ""), // Allow lowercase letters, numbers, '_' and '-'
									)
								}
							/>
							<label htmlFor="slug" className="ps-3 pt-0">
								Slug/url
							</label>
						</FloatLabel>
						<IconSelector
							selectedIcon={selectedIcon}
							setSelectedIcon={setSelectedIcon}
							selectedColor={selectedColor}
						/>
						<ColorSelector selectedColor={selectedColor} setSelectedColor={setSelectedColor} />
					</div>
				</div>

				<div className="row">
					<div className="col-12 fs-5 fw-bold opacity-50 border-bottom pb-2 mb-2 p-0">
						Form example
					</div>
					<VariableSelector availableKeys={availableKeys} handleChipClick={handleChipClick} />
					{selectedKeys.length > 0 ? (
						selectedKeys.map(item => (
							<DisabledFormFields
								item={item}
								disabled={true}
								inputValues={inputValues}
								handleInputChange={handleInputChange}
								handleRemoveInput={handleRemoveInput}
							/>
						))
					) : (
						<div className="col-12">
							<div className="d-flex justify-content-center align-items-center p-5 opacity-50">
								No fields selected
							</div>
						</div>
					)}
				</div>
				<div>
					<div className="col-12 fs-5 fw-bold opacity-50 border-bottom pb-2 mb-2 p-0">
						Upload all templates
					</div>

					<Tooltip target=".custom-choose-btn" content="Choose" position="bottom" />
					<Tooltip target=".custom-upload-btn" content="Upload" position="bottom" />
					<Tooltip target=".custom-cancel-btn" content="Clear" position="bottom" />

					<FileUpload
						ref={fileUploadRef}
						name="demo[]"
						url="/api/upload"
						multiple
						accept=".doc, .docx, .xls, .xlsx"
						maxFileSize={1000000}
						onUpload={onTemplateUpload}
						onSelect={onTemplateSelect}
						onError={onTemplateClear}
						onClear={onTemplateClear}
						headerTemplate={headerTemplate}
						itemTemplate={itemTemplate}
						emptyTemplate={emptyTemplate}
						chooseOptions={chooseOptions}
					/>
				</div>
			</div>

			<div className="col-12 mt-5 text-center border-top pt-3">
				<Button
					label="No"
					icon="pi pi-times"
					onClick={() => setDialogVisible(false)}
					className="mx-3"
					rounded
					text
					severity="secondary"
				/>
				<Button
					label="Yes"
					icon="pi pi-check"
					className="mx-3"
					rounded
					onClick={saveData}
					autoFocus
				/>
			</div>
		</div>
	);
}
